import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { downloadRunFile, type Run, type RunStatus, type Schedule } from './reportsStore'
import { formatRun, nextRuns, summary } from './schedule'
import type { Locale } from './types'

/**
 * "PROGRAMARE SI TRIMITERE" deasupra raportului deschis: propozitia
 * programarii, cui pleaca, oprire / pornire / stergere, urmatoarea trimitere
 * SOCOTITA din regula ("Next: ..."), "Ruleaza acum" si, imediat dupa el, butonul spre ISTORICUL RULARILOR
 * adevarate (din `it_dyn_report_runs`), intr-o fereastra: cand, cum a iesit,
 * cui a plecat, ce fisiere - descarcabile cat sunt in termenul de pastrare.
 */

const STATUS: Record<RunStatus, { ro: string; en: string }> = {
  queued: { ro: 'in coada', en: 'queued' },
  running: { ro: 'in lucru', en: 'running' },
  sent: { ro: 'trimis', en: 'sent' },
  partial: { ro: 'trimis partial', en: 'partly sent' },
  failed: { ro: 'esuat', en: 'failed' },
}

function size(bytes: number): string {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}

export function ScheduleSection({ schedule, runs, locale, now = () => new Date(), onEdit, onToggle, onDelete, onCreate, onRunNow }: {
  schedule: Schedule | null
  runs: Run[]
  locale: Locale
  now?: () => Date
  onEdit(): void
  onToggle(): void
  onDelete(): void
  onCreate(): void
  onRunNow(): void
}) {
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const [confirm, setConfirm] = useState(false)
  const [fileError, setFileError] = useState('')
  const [historyOpen, setHistoryOpen] = useState(false)

  if (!schedule) {
    return (
      <section className="od-rep-delivery od-rep-noprint" aria-label={t('Programare si trimitere', 'Schedule & delivery')}>
        <div className="od-rep-delivery-head">
          <h4>{t('Programare si trimitere', 'Schedule & delivery')}</h4>
        </div>
        <p className="od-rep-meta">
          {t('Raportul nu e programat. Il poti primi pe email zilnic, saptamanal sau lunar.', 'This report is not scheduled. You can get it by email daily, weekly or monthly.')}{' '}
          <button type="button" className="od-rep-link" onClick={onCreate}>
            {t('Programeaza', 'Schedule it')}
          </button>
        </p>
      </section>
    )
  }

  const tz = schedule.timezone || 'Europe/Bucharest'
  const next = schedule.enabled ? nextRuns(schedule, now(), 1)[0] : undefined
  const formats = schedule.formats.map((f) => (f === 'pdf' ? 'PDF' : f === 'xlsx' ? 'Excel' : 'CSV')).join(' + ')
  const busy = runs.some((r) => r.status === 'queued' || r.status === 'running')
  const when = (r: Run) => r.finished_at ?? r.started_at ?? r.queued_at
  /* Cea mai noua sus, oricum ar veni de la server. */
  const ordered = [...runs].sort((a, b) => (when(b) ?? '').localeCompare(when(a) ?? ''))

  return (
    <section className="od-rep-delivery od-rep-noprint" aria-label={t('Programare si trimitere', 'Schedule & delivery')}>
      <div className="od-rep-delivery-head">
        <h4>
          {t('Programare si trimitere', 'Schedule & delivery')}{' '}
          <span className={`od-rep-badge${schedule.enabled ? '' : ' od-rep-badge-off'}`}>{schedule.enabled ? t('Activa', 'Active') : t('Oprita', 'Paused')}</span>
        </h4>
        <span className="od-rep-delivery-tools">
          {confirm ? (
            <span className="od-rep-confirm" role="alert">
              {t('Stergi programarea?', 'Delete the schedule?')}
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => {
                  setConfirm(false)
                  onDelete()
                }}
              >
                {t('Sterge', 'Delete')}
              </button>
              <button type="button" className="btn btn-default btn-sm" onClick={() => setConfirm(false)}>
                {t('Nu', 'No')}
              </button>
            </span>
          ) : (
            <>
              <button type="button" className="btn btn-primary btn-sm od-rep-run-now" disabled={busy} onClick={onRunNow}>
                {busy ? t('In coada...', 'Queued...') : t('Ruleaza acum', 'Run now')}
              </button>
              {runs.length > 0 ? (
                <button type="button" className="btn btn-default btn-sm od-rep-history-open" onClick={() => setHistoryOpen(true)}>
                  {t('Istoricul rularilor', 'Run history')} ({runs.length})
                </button>
              ) : null}
              <button type="button" className="btn btn-default btn-sm" onClick={onEdit}>
                {t('Schimba', 'Edit')}
              </button>
              <button type="button" className="btn btn-default btn-sm" onClick={onToggle}>
                {schedule.enabled ? t('Opreste', 'Pause') : t('Porneste', 'Resume')}
              </button>
              <button type="button" className="btn btn-default btn-sm od-rep-danger" onClick={() => setConfirm(true)}>
                {t('Sterge', 'Delete')}
              </button>
            </>
          )}
        </span>
      </div>
      <div className="od-rep-delivery-grid">
        <div>
          <div className="od-rep-sched-sentence">{summary(schedule, locale)}</div>
          <div className="od-rep-meta">
            {t('Catre', 'To')}: {schedule.recipients.join(', ')}
          </div>
          <div className="od-rep-meta">
            {formats} · {schedule.locale === 'ro' ? t('in romana', 'in Romanian') : t('in engleza', 'in English')}
          </div>
        </div>
        <div>
          {next ? (
            <p className="od-rep-meta od-rep-next-run">
              {t('Urmatoarea', 'Next')}: {formatRun(next, locale, tz)} ({tz})
            </p>
          ) : (
            <p className="od-rep-meta">{t('Oprita: nu e nimic planificat.', 'Paused: nothing is planned.')}</p>
          )}
          {runs.length === 0 ? (
            <p className="od-rep-meta">{t('Nu a rulat inca. Prima trimitere pleaca la ora programata, sau acum, cu "Ruleaza acum".', 'Not run yet. The first email goes out at the scheduled time, or now with "Run now".')}</p>
          ) : null}
        </div>
      </div>
      <Modal id="od-rep-run-history" labelledBy="od-rep-run-history-title" portal open={historyOpen} onClose={() => setHistoryOpen(false)} title={`${t('Istoricul rularilor', 'Run history')} (${runs.length})`}>
        {fileError ? <div className="alert alert-danger">{fileError}</div> : null}
        <div className="od-rep-history-scroll">
          <ul className="od-rep-runs od-rep-history">
            {ordered.map((r) => (
              <li key={r.id} data-run={r.id} data-status={r.status}>
                {when(r) ? formatRun(new Date(when(r)!), locale, tz) : '-'} · <b>{STATUS[r.status]?.[locale] ?? r.status}</b>
                {r.trigger === 'manual' ? ` · ${t('pornita de mana', 'run by hand')}` : ''}
                {r.rows !== null ? ` · ${r.rows} ${t('randuri', 'rows')}` : ''}
                {r.error ? <div className="od-rep-meta od-rep-run-error">{r.error}</div> : null}
                {r.recipients.length > 0 ? (
                  <div className="od-rep-meta od-rep-run-to">
                    {t('Catre', 'To')}: {r.recipients.join(', ')}
                  </div>
                ) : null}
                {r.files.length > 0 ? (
                  <div className="od-rep-run-files">
                    {r.files.map((f) =>
                      f.available ? (
                        <button
                          key={f.id}
                          type="button"
                          className="od-rep-link"
                          onClick={() => {
                            setFileError('')
                            downloadRunFile(f).catch((err) => setFileError(err instanceof Error ? err.message : String(err)))
                          }}
                        >
                          {f.format.toUpperCase()} ({size(f.size)})
                        </button>
                      ) : (
                        <span key={f.id} className="od-rep-meta" title={t('In afara termenului de pastrare', 'Past the retention period')}>
                          {f.format.toUpperCase()} - {t('expirat', 'expired')}
                        </span>
                      )
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </section>
  )
}
